import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import PageEntryPoint from '@databox/ember-integrations/lib/components/Ai/Page/PageEntryPoint';
import type { PageEntryPointProps } from '@databox/ember-integrations/lib/components/Ai/Page/PageEntryPoint';
import type { InitialMessage, OnboardingLeave } from '@databox/types/domains/ai/chat';
import type { ChatMode } from '@databox/types/domains/ai/chat.enums';
import type { ContextObjectBase } from '@databox/types/domains/ai/context';

import type { ReactIntegrations, Service } from 'webapp-types';
import { GenieBookACallOrigin } from 'webapp/enums/ai-chat';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface GenieFullScreenSignature {
	Element: HTMLDivElement;
	Args: {
		contextItems?: ContextObjectBase[];
		threadId?: string | null;
		artifactId?: string | null;
		initialMessages?: InitialMessage[];
		initialPrompt?: string | null;
		onboardingLeave?: OnboardingLeave | null;
		selectedAccountIds?: number[];
		chatMode?: ChatMode | null;
	};
}

export default class GenieFullScreenComponent extends Component<GenieFullScreenSignature> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('leave-guard') declare leaveGuardService: Service.LeaveGuard;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('react') declare reactService: Service.React;
	@service('tour') declare tourService: Service.Tour;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderFullScreen());
	}

	@action
	teardownView() {
		unmountReactRoot(this);

		// TEMPORARY WORKAROUND: Genie's DialogProvider (@ebay/nice-modal-react) overwrites a module-level
		// singleton dispatch on mount. When genie unmounts, dispatch becomes a dead reference, breaking
		// connect's showDialog() calls (e.g. addNewDataset silently fails). Triggering themeChanged forces
		// connect's React root to re-render, reclaiming the dispatch. Proper fix: ensure a single
		// DialogProvider or scope dispatch per React root. See follow-up task in Asana.
		this.eventManagerService.trigger(AppEvent.THEME_CHANGED);
	}

	@action
	navigateToMobileHome() {
		this.navigationService.transitionToRoute('mobile.home');
	}

	// Reconciles rather than remounts, so the mounted Page picks up route-controlled props.
	@action
	handleControlledPropChange() {
		this.renderFullScreen();
	}

	private renderFullScreen() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: PageEntryPointProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService?.model?.firstName ?? '',
			},
			contextItems: this.args.contextItems,
			initialMessages: this.args.initialMessages,
			initialPrompt: this.args.initialPrompt,
			defaultSelectedAccountIds: this.args.selectedAccountIds,
			defaultChatMode: this.args.chatMode ?? undefined,
			trackingOrigin: this.aiChatService.trackingOrigin,
			onActionClick: this.aiChatService.onActionClick,
			showToast: this.aiChatService.showToast,
			onScreenChange: this.aiChatService.onScreenChange,
			onGetThreadId: this.aiChatService.onGetThreadId,
			onActiveThreadChange: this.aiChatService.onThreadChange,
			onThreadNotFound: this.aiChatService.onThreadNotFound,
			onNavigateToRoutine: this.aiChatService.onNavigateToRoutine,
			onNavigateToRun: this.aiChatService.onNavigateToRun,
			// The page shows only the chat's Book a call card, which every plan gets
			onBookACall: this.aiChatService.hasBookingTarget
				? () => this.aiChatService.onBookACall(GenieBookACallOrigin.PAGE)
				: undefined,
			supportEmail: this.aiChatService.supportEmail,
			slack: this.aiChatService.slackHostProps,
			onUpgrade: this.aiChatService.onUpgrade,
			onAddCredits: this.aiChatService.onAddCredits,
			featureConfig: this.aiChatService.featureConfig,
			onClose: this.navigationService.isMobileApp ? this.navigateToMobileHome : undefined,
			threadId: this.args.threadId,
			artifactId: this.args.artifactId,
			onActiveArtifactChange: this.aiChatService.onArtifactChange,
			onArtifactNotFound: this.aiChatService.onArtifactNotFound,
			onNavigateToSettings: this.aiChatService.onNavigateToSettings,
			subscribeToAiEvents: this.aiChatService.subscribeToAiEvents,
			tour: this.tourService.getGenieTourConfig(),
			connectorsPromo: this.aiChatService.connectorsPromoHostProps,
			onboardingLeave: this.args.onboardingLeave,
			registerLeaveGuard: this.leaveGuardService.register,
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				PageEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate this.handleControlledPropChange @threadId @artifactId @onboardingLeave}}
			{{willDestroy this.teardownView}}
			id="genie-container"
			...attributes
		/>
	</template>
}
